import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { changePassword } from "../../services/oprations/authAPI";
import { Lock, Eye, EyeOff } from "lucide-react";

const PasswordManagement = () => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Track which fields are visible
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validatePassword = (password) => {
    const isValidLength = password.length >= 12;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return (
      isValidLength &&
      hasUppercase &&
      hasLowercase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { oldPassword, newPassword, confirmPassword } = formData;

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    if (!validatePassword(newPassword)) {
      toast.error(
        "Password must be at least 12 characters long and include lowercase, uppercase, number, and special character"
      );
      return;
    }

    dispatch(
      changePassword(oldPassword, newPassword, () => {
        setFormData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      })
    );
  };

  const renderPasswordInput = (id, label, placeholder) => (
    <div className="relative flex flex-col">
      <label htmlFor={id} className="text-gray-600 font-medium text-sm mb-1">
        {label}
      </label>
      <input
        id={id}
        type={showPassword[id] ? "text" : "password"}
        value={formData[id]}
        onChange={handleChange}
        placeholder={placeholder}
        className="rounded-2xl p-3 border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition pr-10"
      />
      <button
        type="button"
        onClick={() => toggleShowPassword(id)}
        className="absolute right-3 mt-12 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {showPassword[id] ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-4xl bg-[#f3f7f6] rounded-2xl shadow-lg p-8 space-y-6"
      >
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#001f3f] flex items-center justify-center text-white">
            <Lock size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Change Password
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Enter your current and new password
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          {renderPasswordInput("oldPassword", "Old Password", "Enter old password")}
          {renderPasswordInput("newPassword", "New Password", "Enter new password")}
          {renderPasswordInput("confirmPassword", "Confirm Password", "Confirm new password")}
          <p className="text-xs text-gray-400">
            Password must be at least 12 characters long, including uppercase,
            lowercase, number, and special character.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-[#001f3f] text-white font-semibold px-5 py-3 rounded-xl hover:bg-[#0c1c5c] transition-colors"
          >
            Change Password
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordManagement;
